import axios from 'axios';
import { getApplyAccessToken, getApplyRefreshToken, clearApplyAuth } from '@/utils/applyAuth';

// 在线留言申请人侧独立实例：绝不走 utils/request.js 的全局拦截器（同 memberApply.js 的设计）。
// 留言与会员申请共用 apply 身份（微信扫码授权签发），与管理端/用户端登录态完全隔离。
const BASE_URL = '/api';

const raw = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

raw.interceptors.request.use(config => {
  const token = getApplyAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshing = null;

function redirectToAuth() {
  clearApplyAuth();
  // 带上 redirect：回会员服务页后才会弹「在线留言」弹窗并回跳原页。
  // 缺了它，MemberService 会落到 else 分支误弹「入会申请」弹窗、授权后跳去 /member-apply。
  // 动态取自当前 hash 路由，/contact 与 /contact/my 都能原页返回。
  const back = window.location.hash.replace(/^#/, '') || '/contact';
  window.location.href = '/#/service?needAuth=1&redirect=' + encodeURIComponent(back);
}

async function refreshApplyToken() {
  const refreshToken = getApplyRefreshToken();
  if (!refreshToken) return null;
  const res = await axios.post(`${BASE_URL}/auth/refresh-token`, { token: refreshToken }, {
    headers: { 'Content-Type': 'application/json' }
  });
  if (res.data && res.data.success && res.data.data) {
    const { accessToken } = res.data.data;
    localStorage.setItem('applyAccessToken', accessToken);
    return accessToken;
  }
  return null;
}

/**
 * 鉴权失败后的分流。
 *
 * 留言表单页（/contact）传 { skipAuthRedirect: true }：**不能硬跳** —— 硬跳会刷新页面，
 * 用户刚填的内容全丢。改为只清掉失效 token，把错误抛回页面，由页面就地弹出扫码窗续做。
 */
function handleAuthFailure(skipRedirect) {
  if (skipRedirect) {
    clearApplyAuth();
    return;
  }
  redirectToAuth();
}

raw.interceptors.response.use(
  response => {
    if (response.data && response.data.success) {
      return response.data;
    }
    throw response.data;
  },
  async error => {
    const status = error.response && error.response.status;
    const original = error.config || {};
    const skipRedirect = original.skipAuthRedirect === true;

    // token 过期 / Redis 中身份已失效：用 apply 的 refreshToken 静默续期一次
    if (status === 401 && !original._retry) {
      original._retry = true;
      try {
        if (!refreshing) {
          refreshing = refreshApplyToken().finally(() => { refreshing = null; });
        }
        const newToken = await refreshing;
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return raw(original);
        }
      } catch (e) {
        // 续期失败，走下面的重新授权
      }
      handleAuthFailure(skipRedirect);
      throw error;
    }

    // 无 token / token 无效：安全层直接返 403，同样按上面两条路走
    if (status === 403) {
      handleAuthFailure(skipRedirect);
      throw error;
    }

    throw error;
  }
);

export default {
  /** 提交留言（需微信授权，apply 身份）。表单页传 { skipAuthRedirect: true } */
  submit(data, options = {}) {
    return raw.post('/contact/client/submit', data, options);
  },

  /** 我的留言（按 unionid 归集，含状态） */
  my(options = {}) {
    return raw.get('/contact/client/my', options);
  }
};
